import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import AccountCard from "../shared/AccountCard";
import TransactionRow from "../shared/TransactionRow";
import CustomTooltip from "../shared/CustomTooltip";
import NetWorthProjection from "../shared/NetWorthProjection";
import FinancialCoach from "../shared/FinancialCoach";
import { ACCOUNTS, TRANSACTIONS, SPENDING_DATA, NET_WORTH_DATA, PIE_DATA, fmt } from "../../data/mockData";
import { useIsMobile } from "../../hooks/useIsMobile";

export default function OverviewPage({ netWorth, totalAssets, totalDebt, monthlyChange }) {
  const isMobile = useIsMobile();
  return (
    <div>
      {/* Net Worth Hero */}
      <div style={{
        background: "var(--bg-elevated)",
        border: "1px solid var(--border-strong)", borderRadius: "20px", padding: "32px",
        marginBottom: "24px", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: "-40px", right: "-40px", width: "200px", height: "200px", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)" }} />
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: isMobile ? "16px" : "24px", alignItems: "center" }}>
          <div style={{ gridColumn: isMobile ? "span 2" : "span 2" }}>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "8px" }}>Net Worth</div>
            <div style={{ fontSize: isMobile ? "36px" : "52px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "800", color: "var(--text-primary)", letterSpacing: "-0.04em", lineHeight: 1 }}>{fmt(netWorth)}</div>
            <div style={{ fontSize: "13px", color: "#52D68A", fontFamily: "'JetBrains Mono', monospace", marginTop: "10px" }}>
              ▲ {fmt(monthlyChange)} this month (+{((monthlyChange / (netWorth - monthlyChange)) * 100).toFixed(1)}%)
            </div>
          </div>
          <div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "6px" }}>Total Assets</div>
            <div style={{ fontSize: isMobile ? "20px" : "26px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "#52D68A" }}>{fmt(totalAssets)}</div>
          </div>
          <div>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "6px" }}>Total Debt</div>
            <div style={{ fontSize: isMobile ? "20px" : "26px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "#F87171" }}>{fmt(totalDebt)}</div>
          </div>
        </div>
      </div>

      {/* Financial Coach */}
      <FinancialCoach />

      {/* Charts Row */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 380px", gap: "20px", marginBottom: "24px" }}>
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>Net Worth Trend</div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={NET_WORTH_DATA}>
              <defs>
                <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#D4AF37" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{ fill: "var(--text-faint)", fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="value" name="Net Worth" stroke="#D4AF37" strokeWidth={2} fill="url(#nwGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>Income vs Spending</div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={SPENDING_DATA}>
              <XAxis dataKey="month" tick={{ fill: "var(--text-faint)", fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="income"   name="Income"   stroke="#52D68A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="spending" name="Spending" stroke="#F87171" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>Asset Allocation</div>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <PieChart width={120} height={120}>
              <Pie data={PIE_DATA} cx={55} cy={55} innerRadius={35} outerRadius={55} paddingAngle={3} dataKey="value">
                {PIE_DATA.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
            </PieChart>
            <div style={{ flex: 1 }}>
              {PIE_DATA.map((d, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "2px", background: d.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: "12px", color: "var(--text-muted)" }}>{d.name}</div>
                  <div style={{ fontSize: "12px", fontFamily: "'JetBrains Mono', monospace", color: "var(--text-secondary)" }}>
                    {((d.value / totalAssets) * 100).toFixed(0)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Net Worth Projection */}
      <NetWorthProjection />

      {/* Linked Accounts + Recent Activity */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 380px", gap: "20px" }}>
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>Linked Accounts</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "12px" }}>
            {ACCOUNTS.slice(0, 4).map(a => <AccountCard key={a.id} account={a} />)}
          </div>
        </div>
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>Recent Activity</div>
          {TRANSACTIONS.slice(0, 6).map(tx => <TransactionRow key={tx.id} tx={tx} />)}
        </div>
      </div>
    </div>
  );
}
